// src/components/Header.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectAuth, logout } from "../store/slices/authSlice";
import { showSuccess } from "../store/slices/alertSlice";
import { BrandLogo } from '../branding/BrandAsset';

const Header = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(selectAuth);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 990);

  // Check screen size
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 990);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    await dispatch(logout());
    dispatch(showSuccess('Logged out successfully'));
    navigate('/');
  };

  const handleDropdownEnter = (index) => {
    setActiveDropdown(index);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  // Hide header on screens 990px and below
  if (!isDesktop) {
    return null;
  }

  return (
    <div>
      <style>
        {`
          /* Header Styles */
          .header-section {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            z-index: 1000;
            background-color: #ffffff !important;
            box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          }

          .header-wrapper {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 15px;
            height: 80px;
          }

          /* Logo */
          .logo a {
            display: block;
          }

          .logo img {
            height: 50px;
            width: auto;
          }

          /* Menu */
          .menu-area {
            display: flex;
            align-items: center;
          }

          .menu {
            display: flex;
            list-style: none;
            padding: 0;
            margin: 0;
            gap: 0;
          }

          .menu > li {
            position: relative;
            margin: 0;
            padding: 0;
          }

          .menu > li > .menu-link {
            display: block;
            padding: 10px 16px;
            color: #000000 !important;
            font-weight: 600 !important;
            text-decoration: none;
            transition: all 0.3s ease;
            font-size: 14px;
            letter-spacing: 0.3px;
            border-radius: 6px;
            cursor: pointer;
            background: transparent;
            border: none;
          }

          .menu > li > .menu-link:hover {
            color: #000080 !important;
            background-color: #f0f4ff;
          }

          /* Dropdown Styles */
          .dropdown-menu {
            position: absolute;
            top: calc(100% + 5px);
            left: 50%;
            transform: translateX(-50%) translateY(10px);
            min-width: 220px;
            padding: 8px 0;
            margin: 0;
            background: #ffffff !important;
            border: 1px solid #e9ecef;
            border-radius: 10px;
            box-shadow: 0 15px 50px rgba(0,0,0,0.12);
            opacity: 0;
            visibility: hidden;
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            z-index: 1050;
            list-style: none;
          }

          .dropdown-menu.show {
            opacity: 1;
            visibility: visible;
            transform: translateX(-50%) translateY(0);
          }

          .dropdown-menu .dropdown-item {
            display: block;
            padding: 10px 20px;
            color: #333333 !important;
            font-weight: 500 !important;
            text-decoration: none;
            transition: all 0.2s ease;
            font-size: 14px;
            background: transparent !important;
            border: none;
            width: 100%;
            text-align: left;
            cursor: pointer;
          }

          .dropdown-menu .dropdown-item:hover {
            background-color: #f0f4ff !important;
            color: #000080 !important;
          }

          .dropdown-menu .dropdown-item:first-child {
            border-radius: 10px 10px 0 0;
          }

          .dropdown-menu .dropdown-item:last-child {
            border-radius: 0 0 10px 10px;
          }

          /* Dropdown arrow */
          .dropdown-toggle::after {
            display: inline-block;
            margin-left: 6px;
            vertical-align: middle;
            content: "";
            border-top: 4px solid;
            border-right: 4px solid transparent;
            border-left: 4px solid transparent;
            font-size: 10px;
            transition: transform 0.3s ease;
          }

          .dropdown-toggle.active::after {
            transform: rotate(180deg);
          }

          /* Dropdown divider */
          .dropdown-divider {
            height: 1px;
            margin: 4px 12px;
            background-color: #e9ecef;
          }

          /* Header Actions */
          .header-action {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .header-btn {
            display: inline-block;
          }

          .header-btn .trk-btn {
            text-decoration: none !important;
            padding: 10px 22px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 13px;
            letter-spacing: 0.5px;
            transition: all 0.3s ease;
            display: inline-block;
            border: 2px solid transparent;
            cursor: pointer;
          }

          .trk-btn--primary {
            background-color: #000000 !important;
            color: #ffffff !important;
            border-color: #000000 !important;
          }

          .trk-btn--primary:hover {
            background-color: #000080 !important;
            color: #ffffff !important;
            border-color: #000080 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0,0,128,0.3);
          }

          .trk-btn--dashboard {
            background-color: #000080 !important;
            color: #ffffff !important;
            border-color: #000080 !important;
          }

          .trk-btn--dashboard:hover {
            background-color: #0000a0 !important;
            color: #ffffff !important;
            border-color: #0000a0 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0,0,128,0.3);
          }

          .trk-btn--logout {
            background-color: transparent !important;
            color: #dc3545 !important;
            border-color: #dc3545 !important;
          }

          .trk-btn--logout:hover {
            background-color: #dc3545 !important;
            color: #ffffff !important;
            border-color: #dc3545 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(220,53,69,0.3);
          }

          .header-bar {
            display: none !important;
          }

          @media (max-width: 990px) {
            .header-section {
              display: none !important;
            }
          }
        `}
      </style>

      <header className="header-section">
        <div className="container">
          <div className="header-wrapper">
            <div className="logo">
              <Link to="/">
                <BrandLogo alt="ProTradesInsight" />
              </Link>
            </div>

            <div className="menu-area">
              <ul className="menu">
                <li>
                  <Link to="/" className="menu-link">Home</Link>
                </li>
                
                {/* Forex Dropdown */}
                <li 
                  className="nav-item dropdown"
                  onMouseEnter={() => handleDropdownEnter('forex')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button 
                    className={`menu-link dropdown-toggle ${activeDropdown === 'forex' ? 'active' : ''}`}
                    onClick={() => handleDropdownEnter('forex')}
                  >
                    Forex
                  </button>
                  <ul className={`dropdown-menu ${activeDropdown === 'forex' ? 'show' : ''}`}>
                    <li><Link to="/forex-signal" className="dropdown-item">Forex Signals</Link></li>
                    <li><a href="#" className="dropdown-item">Forex Brokers</a></li>
                  </ul>
                </li>

                {/* Crypto Dropdown */}
                <li 
                  className="nav-item dropdown"
                  onMouseEnter={() => handleDropdownEnter('crypto')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button 
                    className={`menu-link dropdown-toggle ${activeDropdown === 'crypto' ? 'active' : ''}`}
                    onClick={() => handleDropdownEnter('crypto')}
                  >
                    Crypto
                  </button>
                  <ul className={`dropdown-menu ${activeDropdown === 'crypto' ? 'show' : ''}`}>
                    <li><Link to="/crypto-signal" className="dropdown-item">Crypto Signals</Link></li>
                    <li><a href="#" className="dropdown-item">Crypto Trading Mastery</a></li>
                  </ul>
                </li>

                {/* Courses Dropdown */}
                <li 
                  className="nav-item dropdown"
                  onMouseEnter={() => handleDropdownEnter('courses')}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button 
                    className={`menu-link dropdown-toggle ${activeDropdown === 'courses' ? 'active' : ''}`}
                    onClick={() => handleDropdownEnter('courses')}
                  >
                    Courses
                  </button>
                  <ul className={`dropdown-menu ${activeDropdown === 'courses' ? 'show' : ''}`}>
                    <li><a href="#" className="dropdown-item">Beginner to Advanced</a></li>
                    <li><a href="#" className="dropdown-item">Personal Strategies</a></li>
                    <li><a href="#" className="dropdown-item">Mentorship</a></li>
                  </ul>
                </li>

                <li>
                  <Link to="/services" className="menu-link">Services</Link>
                </li>
                <li>
                  <Link to="/about" className="menu-link">About</Link>
                </li>
                <li>
                  <Link to="/contact" className="menu-link">Contact</Link>
                </li>
                <li>
                  <Link to="/ebooks" className="menu-link">Ebooks</Link>
                </li>
              </ul>
            </div>

            <div className="header-action">
              {isAuthenticated ? (
                <>
                  <div className="header-btn">
                    <Link to="/dashboard" className="trk-btn trk-btn--dashboard">
                      GO TO DASHBOARD
                    </Link>
                  </div>
                  <div className="header-btn">
                    <button onClick={handleLogout} className="trk-btn trk-btn--logout">
                      LOGOUT
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="header-btn">
                    <Link to="/signup" className="trk-btn trk-btn--primary">
                      SIGN UP
                    </Link>
                  </div>
                  <div className="header-btn">
                    <Link to="/login" className="trk-btn trk-btn--primary">
                      LOG IN
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Header;
